import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import StatCard from '../../components/StatCard';
import ActivityItem from '../../components/ActivityItem';
import Layout from '../../components/Layout';
import LiabilitiesPanel from '../../components/LiabilitiesPanel';
import DashboardNotificationsBar from '../../components/planner/DashboardNotificationsBar';
import { Button } from '../../components/ui/Button';
import { dashboardAPI, estimatesAPI } from '../../api';
import { formatActivityRelativeTime } from '../../lib/formatRelativeTime';
import { FileText, Users, Receipt, ShoppingCart, ChevronRight } from 'lucide-react';
import { portalFeedbackActionLabel } from '../../lib/estimateClientPortalFeedback';

const activityNumLinkClassName =
  'text-blue-600 dark:text-blue-400 hover:underline underline-offset-2 font-medium';

function ActivityNumLink({ to, children }) {
  if (!to) return children;
  return (
    <Link to={to} className={activityNumLinkClassName}>
      {children}
    </Link>
  );
}

/** Rich activity line with the document reference linked to the tenant editor when possible. */
function renderActivityTitle(activity) {
  const ref = activity.referenceNum ?? '';
  switch (activity.type) {
    case 'invoice': {
      const href = activity.id ? `/tenant/invoices/${activity.id}` : null;
      const tail =
        activity.status === 'sent'
          ? ' sent'
          : activity.status === 'paid-in-full'
            ? ' paid'
            : ' created';
      return (
        <>
          Invoice
          {ref ? (
            <>
              {' '}
              <ActivityNumLink to={href}>{ref}</ActivityNumLink>
            </>
          ) : null}
          {tail}
        </>
      );
    }
    case 'estimate': {
      const href = activity.id ? `/tenant/estimates/${activity.id}` : null;
      const tail = activity.status === 'sent' ? ' sent' : ' created';
      return (
        <>
          Estimate
          {ref ? (
            <>
              {' '}
              <ActivityNumLink to={href}>{ref}</ActivityNumLink>
            </>
          ) : null}
          {tail}
        </>
      );
    }
    case 'payment': {
      const href = activity.invoiceId ? `/tenant/invoices/${activity.invoiceId}` : null;
      const stripe = activity.paymentMethod === 'stripe' ? ' (Stripe)' : '';
      return (
        <>
          Payment received for Invoice{' '}
          {ref ? <ActivityNumLink to={href}>{ref}</ActivityNumLink> : '—'}
          {stripe}
        </>
      );
    }
    case 'purchase': {
      const href = activity.id ? `/tenant/purchases/${activity.id}` : null;
      const tail = activity.status === 'received' ? ' received' : ' ordered';
      return (
        <>
          Purchase
          {ref ? (
            <>
              {' '}
              <ActivityNumLink to={href}>{ref}</ActivityNumLink>
            </>
          ) : null}
          {tail}
        </>
      );
    }
    case 'client_portal': {
      const text = activity.title || 'Client portal activity';
      const href = activity.invoiceId
        ? `/tenant/invoices/${activity.invoiceId}`
        : activity.estimateId
          ? `/tenant/estimates/${activity.estimateId}`
          : null;
      if (href && ref && text.includes(ref)) {
        const i = text.indexOf(ref);
        return (
          <>
            {text.slice(0, i)}
            <ActivityNumLink to={href}>{ref}</ActivityNumLink>
            {text.slice(i + ref.length)}
          </>
        );
      }
      return text;
    }
    default:
      return ref || 'Activity';
  }
}

export default function TenantDashboard() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    financialYearTurnoverToDate: 0,
    outstandingBalance: 0,
    pendingEstimates: 0,
    overdueInvoices: 0,
    currentMonthRevenue: 0,
    unreadClientMessagesCount: 0,
    unacknowledgedEstimateClientResponses: [],
  });
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch dashboard stats
        const statsRes = await dashboardAPI.getStats();
        setStats(statsRes.stats || {});
        setMonthlyRevenue(statsRes.monthlyRevenue || []);

        // Fetch recent activity
        const activityRes = await dashboardAPI.getActivity(10);
        setRecent(activityRes.activities || []);
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const formatCurrencyWhole = (value) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(Number(value) || 0));

  const monthOnMonthRevenueDeltaPct = useMemo(() => {
    if (!monthlyRevenue?.length || monthlyRevenue.length < 2) return undefined;
    const prev = Number(monthlyRevenue[monthlyRevenue.length - 2]?.revenue) || 0;
    const cur = Number(monthlyRevenue[monthlyRevenue.length - 1]?.revenue) || 0;
    if (prev === 0) return cur === 0 ? 0 : undefined;
    return Math.round(((cur - prev) / prev) * 100);
  }, [monthlyRevenue]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DashboardNotificationsBar />
        {stats.unreadClientMessagesCount > 0 && (
          <div className="mb-6 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-950 dark:text-amber-100">
              You have <strong>{stats.unreadClientMessagesCount}</strong> unread{' '}
              {stats.unreadClientMessagesCount === 1 ? 'message' : 'messages'} from the client portal.
            </p>
            <Link
              to="/tenant/client-portal-messages"
              className="inline-flex items-center justify-center rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 no-underline shrink-0"
            >
              View messages
            </Link>
          </div>
        )}
        {Array.isArray(stats.unacknowledgedEstimateClientResponses) &&
          stats.unacknowledgedEstimateClientResponses.length > 0 && (
            <div className="mb-6 rounded-lg border border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-950/35 px-4 py-3 space-y-3">
              <p className="text-sm font-medium text-sky-950 dark:text-sky-100">
                Client updates on estimates
              </p>
              <ul className="space-y-2 text-sm text-sky-900 dark:text-sky-100">
                {stats.unacknowledgedEstimateClientResponses.map((row) => {
                  const action = row?.clientPortalResponse?.action;
                  const label = portalFeedbackActionLabel(action);
                  return (
                    <li
                      key={row.id}
                      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-t border-sky-200/80 dark:border-sky-800 pt-2 first:border-t-0 first:pt-0"
                    >
                      <span>
                        Estimate{' '}
                        <Link
                          to={`/tenant/estimates/${row.id}`}
                          className="font-semibold text-sky-800 dark:text-sky-200 underline-offset-2 hover:underline"
                        >
                          {row.estimateNum || row.id}
                        </Link>
                        : {label.toLowerCase()} via the client link.
                      </span>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Link
                          to={`/tenant/estimates/${row.id}`}
                          className="inline-flex items-center justify-center rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium px-3 py-1.5 no-underline"
                        >
                          View estimate
                        </Link>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await estimatesAPI.acknowledgeClientResponse(row.id);
                              setStats((prev) => ({
                                ...prev,
                                unacknowledgedEstimateClientResponses: (
                                  prev.unacknowledgedEstimateClientResponses || []
                                ).filter((x) => x.id !== row.id),
                              }));
                            } catch (e) {
                              console.error(e);
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-md border border-sky-600 text-sky-800 dark:text-sky-200 text-xs font-medium px-3 py-1.5 bg-white/60 dark:bg-sky-950/40 hover:bg-white dark:hover:bg-sky-900/50"
                        >
                          Dismiss
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        {/* Stat Cards: 3 main + compact pair stacked on the right (lg) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 lg:items-stretch">
          <StatCard 
            title="Turnover (FY to date)" 
            value={formatCurrencyWhole(stats.financialYearTurnoverToDate)} 
            accent="green"
          />
          <StatCard 
            title="This Month Revenue" 
            value={formatCurrencyWhole(stats.currentMonthRevenue)} 
            accent="green"
            delta={monthOnMonthRevenueDeltaPct}
          />
          <StatCard 
            title="Outstanding Balance" 
            value={formatCurrencyWhole(stats.outstandingBalance)} 
            accent="orange"
          />
          <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-1 lg:h-full lg:justify-center lg:min-w-0">
            <StatCard 
              compact
              title="Pending Estimates" 
              value={stats.pendingEstimates} 
              accent="yellow"
            />
            <StatCard 
              compact
              title="Overdue Invoices" 
              value={stats.overdueInvoices} 
              accent="red"
            />
          </div>
        </div>

        {/* 12-Month income vs outgoings (invoice payments vs purchase totals) */}
        {monthlyRevenue.length > 0 && (
          <div className="bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)] mb-6">
            <div className="px-6 py-4 border-b border-[var(--ds-border-divider)]">
              <h3 className="text-h2 text-[var(--ds-text-primary)] font-semibold">
                12-Month Income &amp; Outgoings
              </h3>
              <p className="text-meta text-[var(--ds-text-muted)] mt-1">
                Income: payments received on invoices. Outgoings: purchase totals by purchase date.
              </p>
            </div>
            <div className="p-6">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={monthlyRevenue} margin={{ bottom: 4, left: 8, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                  <XAxis 
                    dataKey="monthLabel" 
                    stroke="#6b7280"
                    className="dark:stroke-gray-400"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    interval={0}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    className="dark:stroke-gray-400"
                    tick={{ fill: '#6b7280' }}
                    tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--ds-surface-panel, #fff)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => [formatCurrency(value), name]}
                    labelFormatter={(_label, payload) => {
                      const row = payload?.[0]?.payload;
                      if (row?.month) {
                        return new Date(`${row.month}T12:00:00`).toLocaleDateString('en-GB', {
                          month: 'short',
                          year: 'numeric',
                        });
                      }
                      return _label;
                    }}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 12 }} />
                  <Bar
                    name="Income"
                    dataKey="revenue"
                    fill="#10b981"
                    radius={[6, 6, 0, 0]}
                    className="dark:fill-green-500"
                  />
                  <Bar
                    name="Outgoings"
                    dataKey="outgoings"
                    fill="#f97316"
                    radius={[6, 6, 0, 0]}
                    className="dark:fill-orange-500"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)]">
            <div className="px-4 py-3">
              <h3 className="text-h2 text-[var(--ds-text-primary)] font-semibold">Recent Activity</h3>
            </div>
            <div>
              {loading ? (
                <div className="px-4 pb-4 pt-0 text-meta text-[var(--ds-text-muted)]">Loading...</div>
              ) : recent.length === 0 ? (
                <div className="px-4 pb-4 pt-0 text-meta text-[var(--ds-text-muted)]">No recent activity</div>
              ) : (
                recent.map((activity, idx) => {
                  const at = activity.activityDate ?? activity.createdAt;
                  return (
                    <ActivityItem 
                      key={`${activity.type}-${activity.id}-${idx}`}
                      rowClassName="px-4 py-2 sm:px-4 sm:py-2"
                      titleClassName="text-[12px] leading-snug"
                      titleContent={renderActivityTitle(activity)}
                      subtitle={
                        at
                          ? formatActivityRelativeTime(at)
                          : undefined
                      }
                      subtitleFirst
                      subtitleTooltip={
                        at
                          ? new Date(at).toLocaleString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : undefined
                      }
                    />
                  );
                })
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)]">
            <div className="px-6 py-4 border-b border-[var(--ds-border-divider)]">
              <h3 className="text-h2 text-[var(--ds-text-primary)] font-semibold">Quick Actions</h3>
            </div>
            <div className="p-4 space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/invoices/new')}
                className="w-full justify-between h-8 text-xs"
              >
                <span className="flex items-center"><FileText className="mr-3 h-5 w-5" />Create New Invoice</span>
                <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/estimates/new')}
                className="w-full justify-between h-8 text-xs"
              >
                <span className="flex items-center"><Receipt className="mr-3 h-5 w-5" />Create Estimate</span>
                <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/clients')}
                className="w-full justify-between h-8 text-xs"
              >
                <span className="flex items-center"><Users className="mr-3 h-5 w-5" />Manage Clients</span>
                <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/purchases/new')}
                className="w-full justify-between h-8 text-xs"
              >
                <span className="flex items-center"><ShoppingCart className="mr-3 h-5 w-5" />Record Purchase</span>
                <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
              </Button>
            </div>
          </div>
        </div>

        {/* Liabilities roll-up (credit cards, loans, drawings owed back) */}
        <div className="mt-5">
          <LiabilitiesPanel />
        </div>
      </div>
    </Layout>
  );
}
